import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  User, Building, HardHat, QrCode, ArrowSquareOut, IdentificationBadge,
  Eye, Buildings, FileText, Wrench, EnvelopeSimple, Camera, Clock, ListChecks,
  CurrencyEur, UsersThree, Lock,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_TABS = [
  { id: 'salarie',   label: 'Vue Salarié',       icon: HardHat,             color: '#15803D' },
  { id: 'client',    label: 'Vue Client',        icon: User,                color: '#002FA7' },
  { id: 'soustrait', label: 'Vue Sous-traitant', icon: Building,            color: '#7C3AED' },
  { id: 'scan',      label: 'Station Scan',      icon: QrCode,              color: '#DC2626' },
];

const Section = ({ title, icon: Icon, color, children }) => (
  <div style={{ background: 'white', border: '1px solid #E2E2E6', borderRadius: 12, padding: '14px 18px', marginBottom: 14 }}>
    <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 10px', color, fontSize: '1rem' }}>
      <Icon size={18} weight="bold" /> {title}
    </h3>
    {children}
  </div>
);

const ScreenLink = ({ to, title, desc, icon: Icon, color }) => (
  <Link to={to} target="_blank" style={{ textDecoration: 'none', display: 'block', padding: '10px 12px', background: '#F8F9FB', border: '1px solid #E2E2E6', borderRadius: 8, marginBottom: 8, transition: 'transform 120ms ease' }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.background = '#EFF6FF'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.background = '#F8F9FB'; }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Icon size={20} color={color || '#5E5F66'} />
      <div style={{ flex: 1 }}>
        <div style={{ color: '#0A0B10', fontWeight: 700, fontSize: '0.92rem' }}>{title}</div>
        <div style={{ color: '#5E5F66', fontSize: '0.8rem', marginTop: 2 }}>{desc}</div>
      </div>
      <ArrowSquareOut size={16} color="#9CA3AF" />
    </div>
  </Link>
);

const IframePreview = ({ src, label, height = 520 }) => (
  <div style={{ border: '2px solid #E2E2E6', borderRadius: 12, overflow: 'hidden', position: 'relative', background: '#F4F5F7' }}>
    <div style={{ padding: '6px 12px', background: '#FEF3C7', color: '#9A3412', fontSize: '0.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
      <Eye size={12} /> APERCU — {label}
    </div>
    <iframe
      title={label}
      src={src}
      style={{ width: '100%', height, border: 'none', display: 'block', background: 'white' }}
      sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
    />
  </div>
);

// -----------------------------------------------------------------------------
const ImpersonatePicker = () => {
  const [emps, setEmps] = useState([]);
  const [selected, setSelected] = useState('');
  const [openPath, setOpenPath] = useState('/mes-chantiers');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/users/employees`, { withCredentials: true })
      .then(res => { if (!cancelled) { setEmps(res.data || []); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const doImpersonate = async () => {
    if (!selected) return toast.error('Selectionnez un salarie');
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/impersonate`, { user_id: selected }, { withCredentials: true });
      const url = `${openPath}?imp=${encodeURIComponent(res.data.token)}`;
      window.open(url, '_blank');
      toast.success(`Session aperçu ouverte (30 min, lecture seule)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <Section title="Voir l'application en tant que..." icon={UsersThree} color="#7C3AED">
      <p style={{ marginTop: 0, fontSize: '0.88rem', color: '#5E5F66' }}>
        Ouvre l'app dans un nouvel onglet avec une <strong>session aperçu read-only</strong> du
        salarie choisi (30 min). Pratique pour debugger un retour utilisateur ou onboarder.
        Les ecritures (POST/PUT/DELETE) sont automatiquement bloquees par le backend.
      </p>
      {loading ? (
        <p style={{ fontSize: '0.85rem', color: '#5E5F66' }}>Chargement...</p>
      ) : emps.length === 0 ? (
        <p style={{ fontSize: '0.85rem', color: '#5E5F66' }}>Aucun salarie disponible.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontSize: '0.78rem', color: '#5E5F66', fontWeight: 600 }}>Salarie</label>
            <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="impersonate-emp-select"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E2E6', borderRadius: 6, marginTop: 4, fontSize: '0.88rem' }}>
              <option value="">-- Choisir --</option>
              {emps.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.78rem', color: '#5E5F66', fontWeight: 600 }}>Page de destination</label>
            <select value={openPath} onChange={(e) => setOpenPath(e.target.value)} data-testid="impersonate-page-select"
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #E2E2E6', borderRadius: 6, marginTop: 4, fontSize: '0.88rem' }}>
              <option value="/mes-chantiers">Mes chantiers</option>
              <option value="/my-equipment">Mon materiel</option>
              <option value="/scan-materiel">Scan materiel</option>
              <option value="/time-entry">Pointage</option>
              <option value="/dashboard">Dashboard</option>
            </select>
          </div>
        </div>
      )}
      <button onClick={doImpersonate} disabled={busy || !selected} className="btn btn-primary btn-sm" data-testid="impersonate-btn"
        style={{ marginTop: 10, background: '#7C3AED' }}>
        <Eye size={14} /> {busy ? 'Ouverture...' : 'Ouvrir en mode aperçu'}
      </button>
      <div style={{ marginTop: 10, padding: '6px 10px', background: '#FEF3C7', borderRadius: 6, fontSize: '0.74rem', color: '#92400E', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Lock size={11} /> Session lecture seule, expire dans 30 min, trace via JWT (audit admin).
      </div>
    </Section>
  );
};

const SalarieView = () => (
  <>
    <ImpersonatePicker />
    <Section title="Ecrans accessibles aux salaries" icon={HardHat} color="#15803D">
      <p style={{ marginTop: 0, color: '#5E5F66', fontSize: '0.88rem' }}>
        Le salarie est limite a la consultation de SES projets et SON materiel/vehicule, et a la
        publication d'avancement chantier. Tout est en lecture seule, sauf le pointage, le
        scan materiel et le fil d'actualite chantier.
      </p>
      <ScreenLink to="/mes-chantiers" icon={Buildings} color="#002FA7"
        title="Mes chantiers"
        desc="Liste des chantiers affectes - 5 sous-onglets : Fil d'avancement (publier photos), Galerie photos groupee, Documents, Liste de courses, SAV/Reserves." />
      <ScreenLink to="/my-equipment" icon={Wrench} color="#1565C0"
        title="Mon materiel"
        desc="Materiel affecte - clic sur une carte ouvre fiche detail (photos + infos + bouton Signaler un probleme)." />
      <ScreenLink to="/scan-materiel" icon={QrCode} color="#DC2626"
        title="Scan entrees / sorties"
        desc="Identification par QR (carte salarie) puis scan multi-QR du materiel + signature." />
      <ScreenLink to="/time-entry" icon={Clock} color="#7C3AED"
        title="Pointage"
        desc="Multi-pointage par jour, sans rechargement. Photos d'avancement optionnelles." />
      <ScreenLink to="/my-documents" icon={FileText} color="#5E5F66"
        title="Mes documents (contrat, fiches de paie, RIB)"
        desc="Acces a ses propres documents administratifs." />
    </Section>
    <Section title="Carte d'identification (badge QR)" icon={IdentificationBadge} color="#002FA7">
      <p style={{ marginTop: 0, fontSize: '0.88rem' }}>
        Chaque salarie peut telecharger sa carte au format A4 (photo + nom + fonction + QR personnel)
        depuis <strong>Mon materiel</strong>. Cette carte sert d'identification a la station de scan.
      </p>
      <code style={{ display: 'block', padding: '8px 12px', background: '#F4F5F7', borderRadius: 6, fontSize: '0.78rem' }}>GET /api/users/{'{user_id}'}/badge → PDF</code>
    </Section>
  </>
);

const ClientView = () => {
  const demoUrl = `${window.location.origin}/client/demo`;
  return (
    <>
      <Section title="Portail public client" icon={EnvelopeSimple} color="#002FA7">
        <p style={{ marginTop: 0, color: '#5E5F66', fontSize: '0.88rem' }}>
          Le client n'a pas d'acces direct a l'application. Il recoit ses devis, factures, situations,
          avoirs et le <strong>DGD + facture de solde</strong> par email (via Resend), tous accompagnes
          des PDFs en pieces jointes.
        </p>
        <div style={{ padding: '10px 14px', background: '#DBEAFE', border: '1px solid #BFDBFE', borderRadius: 8, fontSize: '0.84rem', color: '#1E40AF', marginBottom: 10 }}>
          <strong>Mode demo client</strong> ci-dessous : aperçu d'un suivi de chantier client
          (timeline + factures + DGD) que l'on peut partager comme argument commercial.
        </div>
        <code style={{ display: 'block', padding: '8px 12px', background: '#F4F5F7', borderRadius: 6, fontSize: '0.78rem', marginBottom: 10 }}>{demoUrl}</code>
        <a href={demoUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" data-testid="open-client-portal">
          <ArrowSquareOut size={14} /> Ouvrir la demo dans un nouvel onglet
        </a>
      </Section>
      <Section title="Apercu live (mode demo)" icon={Eye} color="#002FA7">
        <IframePreview src="/client/demo" label="Portail client - mode demo" height={680} />
      </Section>
    </>
  );
};

const SousTraitantView = () => {
  const demoUrl = `${window.location.origin}/st/demo`;
  return (
    <>
      <Section title="Portail public sous-traitant" icon={Building} color="#7C3AED">
        <p style={{ marginTop: 0, color: '#5E5F66', fontSize: '0.88rem' }}>
          Les sous-traitants accedent au portail via un <strong>lien unique</strong> recu par email
          (token signe par consultation). Ils peuvent y deposer leur chiffrage, telecharger les
          documents et echanger par messagerie. Pas de mot de passe : authentification par lien.
        </p>
        <div style={{ padding: '10px 14px', background: '#EDE9FE', border: '1px solid #DDD6FE', borderRadius: 8, fontSize: '0.84rem', color: '#5B21B6', marginBottom: 10 }}>
          <strong>Mode invite</strong> ci-dessous : aperçu interactif du portail avec des donnees
          d'exemple. Ideal pour montrer le portail a un nouveau sous-traitant avant de l'inviter.
        </div>
        <code style={{ display: 'block', padding: '8px 12px', background: '#F4F5F7', borderRadius: 6, fontSize: '0.78rem', marginBottom: 10 }}>{demoUrl}</code>
        <a href={demoUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" data-testid="open-st-portal">
          <ArrowSquareOut size={14} /> Ouvrir la demo dans un nouvel onglet
        </a>
      </Section>
      <Section title="Apercu live (mode invite)" icon={Eye} color="#7C3AED">
        <IframePreview src="/st/demo" label="Portail sous-traitant - mode invite" height={620} />
      </Section>
    </>
  );
};

const ScanView = () => (
  <>
    <Section title="Station de scan materiel" icon={QrCode} color="#DC2626">
      <p style={{ marginTop: 0, color: '#5E5F66', fontSize: '0.88rem' }}>
        Interface dediee aux <strong>entrees et sorties de materiel</strong>. Le salarie s'identifie
        avec sa carte QR, choisit Sortie ou Retour, scanne les materiels un a un avec son
        telephone, et confirme avec sa signature. Les affectations sont <strong>mises a jour
        automatiquement</strong>, les degats deviennent des signalements admin.
      </p>
      <ul style={{ fontSize: '0.85rem', paddingLeft: 18 }}>
        <li><strong>5 etapes</strong> : Identification → Type → Scan items → Recap + signature → Confirmation</li>
        <li>Code couleur etat : vert (Neuf/Bon) → jaune (Usage) → orange (A reparer) → rouge (HS)</li>
        <li>Auto-signalement quand orange / rouge : notification admin + report ouvert</li>
      </ul>
      <ScreenLink to="/scan-materiel" icon={QrCode} color="#DC2626"
        title="Ouvrir la station de scan"
        desc="Necessite une camera. Ideal sur tablette dediee au comptoir entree/sortie." />
    </Section>
    <Section title="Apercu en direct" icon={Eye} color="#DC2626">
      <IframePreview src="/scan-materiel" label="Station scan" height={620} />
    </Section>
  </>
);

// -----------------------------------------------------------------------------
const InterfacePreviewsPage = () => {
  const [tab, setTab] = useState('salarie');
  return (
    <div data-testid="interface-previews-page" style={{ maxWidth: 980, margin: '0 auto' }}>
      <div className="page-header" style={{ marginBottom: 18 }}>
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Eye size={26} weight="bold" /> Apercus des interfaces
        </h1>
        <p className="page-subtitle">Visualisez ce que voient les autres roles (client, sous-traitant, salarie, station scan).</p>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: '2px solid #E2E2E6', overflowX: 'auto', flexWrap: 'wrap' }}>
        {ROLE_TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`previews-tab-${t.id}`}
            style={{
              padding: '10px 16px', border: 'none', cursor: 'pointer',
              backgroundColor: 'transparent', fontSize: '0.92rem', fontWeight: 700,
              borderBottom: tab === t.id ? `3px solid ${t.color}` : '3px solid transparent',
              color: tab === t.id ? t.color : '#5E5F66', marginBottom: -2,
              display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
            }}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'salarie'   && <SalarieView />}
      {tab === 'client'    && <ClientView />}
      {tab === 'soustrait' && <SousTraitantView />}
      {tab === 'scan'      && <ScanView />}
    </div>
  );
};

export default InterfacePreviewsPage;
